// NOW · Command Deck — the advisor's day in one screen, computed from real data only:
//   briefing   the day written as five sentences, every name a live link, "Start Flow" to act on it
//   capture    type naturally, AVRASTRO understands (Greek + English)
//   health     one number for the whole book, explained by five factors
//   constellation  the book as a living map: closer to the centre = needs you sooner, size = portfolio
//   attention  who · why · next move;  today  a timeline with a live now-line;  cases;  moments
import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { attentionByPerson, bookHealth, dailyBriefing, pendingDecisions, type BriefingLine, type AttentionItem } from '@vero/intelligence';
import { isTerminalStage, stageIndex, caseAgeDays, isStalled, isActivePolicy, annualNow, CASE_MAIN_STAGES, type Task } from '@vero/domain';
import { useRuntime, type Runtime } from '../core/runtime.tsx';
import { Avatar, Empty, dayPart, reasonText } from '../core/ui.tsx';
import { Icon, KIND_ICON } from '../core/icons.tsx';
import { navigate, personPath } from '../core/router.ts';
import { Dial, Band, FactorBars, Constellation, BAND_COLOR, TIER_VAR, type Star } from '../core/viz.tsx';
import { AttentionCard, performAction } from './attention.tsx';
import { openers } from './forms.tsx';
import { CaptureBox } from './capture.tsx';
import { startFlow, flowQueue } from './flow.tsx';
import { SetupCard } from './setup.tsx';
import { NextActionsCard } from './next-actions.tsx';
import { LifeRadarCard } from './life-radar.tsx';
import { VideoButton } from './video.tsx';

const hhmm = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
function useClock(): string {
  const [now, setNow] = useState(hhmm);
  useEffect(() => { const id = setInterval(() => setNow(hhmm()), 30_000); return () => clearInterval(id); }, []);
  return now;
}

export function NowView() {
  const rt = useRuntime();
  const { t, i18n, view, today, attention } = rt;
  const clock = useClock();
  const [showAll, setShowAll] = useState(false);
  const grouped = useMemo(() => [...attentionByPerson(attention.filter((a) => (a.rule !== 'birthday' && a.rule !== 'nameday') || a.severity !== 'low')).values()], [attention]);
  const visible = showAll ? grouped : grouped.slice(0, 6);
  const todayTasks = useMemo(() => view.rows('tasks').filter((x) => x.dueDate === today && x.status !== 'cancelled').sort((a, b) => (a.dueTime ?? '00').localeCompare(b.dueTime ?? '00')), [view, today]);
  const overdue = useMemo(() => view.rows('tasks').filter((x) => x.status === 'open' && x.dueDate && x.dueDate < today), [view, today]);
  const cases = useMemo(() => view.rows('cases').filter((c) => !isTerminalStage(c.stage)).sort((a, b) => caseAgeDays(b, today) - caseAgeDays(a, today)), [view, today]);
  const moments = attention.filter((a) => a.rule === 'birthday' || a.rule === 'nameday' || a.rule === 'child_milestone');
  const briefing = useMemo(() => dailyBriefing(view, today, attention, clock), [view, today, attention, clock]);
  const book = useMemo(() => bookHealth(view, today), [view, today]);
  const stars = useMemo(() => buildStars(view, i18n, attention, today), [view, attention, i18n]);
  const queueSize = useMemo(() => flowQueue(attention).length, [attention]);
  const decisions = useMemo(() => pendingDecisions(view, today), [view, today]);
  const waiting = useMemo(() => view.rows('tasks').filter((x) => x.status === 'open' && x.origin === 'waiting_client').sort((a, b) => (a.dueDate ?? '').localeCompare(b.dueDate ?? '')), [view]);
  const name = (rt.advisor.name || '').split(' ')[0];
  const openPerson = useCallback((id: string) => navigate(personPath(id)), []);
  const hasPeople = view.rows('persons').length > 0;

  return (
    <div data-testid="now">
      {rt.engine.startsWith('memory') && <div className="banner" role="status"><span>{t('boot.volatile')}</span><button className="btn btn-sm" onClick={() => navigate('/data')}>{t('data.backup_title')}</button></div>}
      {rt.demo && <div className="banner"><span>{t('demo.banner')}</span><button className="btn btn-sm" onClick={() => navigate('/data')}>{t('nav.data')}</button></div>}

      <header className="deck-hero">
        <div>
          <div className="date">{i18n.date(today, 'long')} · <span className="num">{i18n.time(clock)}</span></div>
          <h1>{t('today.greeting', { part: dayPart(), name: name || 'none' })}</h1>
          <p>{t('today.summary', { attention: grouped.length, agenda: todayTasks.filter((x) => x.status === 'open').length })}</p>
        </div>
        <div className="page-actions">
          <button className="btn btn-flow btn-lg" onClick={() => startFlow(rt)} disabled={!queueSize} data-testid="start-flow"><Icon name="flow" />{t('flow.start')}{queueSize > 0 && <span className="chip" style={{ background: 'rgba(255,255,255,.18)', color: '#fff', border: 0, height: 20 }}>{queueSize}</span>}</button>
          <button className="btn btn-lg" onClick={() => openers(rt).person()}><Icon name="plus" />{t('people.new')}</button>
        </div>
      </header>

      <SetupCard />

      <div style={{ marginBottom: 18 }}><CaptureBox /></div>

      <div className="deck-top">
        <section className="card briefing" data-testid="briefing" aria-labelledby="brief-title">
          <div className="briefing-head"><span className="spark"><Icon name="spark" size={16} /></span><h2 id="brief-title">{t('briefing.title')}</h2></div>
          <ul className="briefing-lines">{briefing.map((l, i) => <li key={i}><span className={`tone ${l.tone}`} aria-hidden="true" /><span>{renderLine(rt, l)}</span></li>)}</ul>
          <div className="briefing-foot">
            <span className="tiny muted">{t('briefing.generated', { time: i18n.time(clock) })}</span>
            {queueSize > 0 && <button className="btn btn-sm btn-flow" onClick={() => startFlow(rt)}><Icon name="flow" size={14} />{t('briefing.start_flow')} · {queueSize}</button>}
          </div>
        </section>

        <section className="card health-card" data-testid="book-health">
          <div className="row spread"><h3 style={{ margin: 0, fontSize: 15 }}>{t('health.book')}</h3>{book.clients > 0 && <Band band={book.band} label={t(`health.band.${book.band}`)} />}</div>
          {book.clients ? <>
            <div className="health-top">
              <Dial score={book.score} band={book.band} label="/100" testId="book-dial" />
              <div className="grow"><FactorBars rows={(['coverage', 'engagement', 'review', 'open_items', 'profile'] as const).map((k) => ({ key: k, label: t(`health.factor.${k}`), value: book.factors[k], max: 100 }))} /></div>
            </div>
            <div className="distbar" role="img" aria-label={(['strong', 'steady', 'watch', 'at_risk'] as const).map((b) => `${t(`health.band.${b}`)} ${book.distribution[b]}`).join(', ')}>
              {(['strong', 'steady', 'watch', 'at_risk'] as const).filter((b) => book.distribution[b]).map((b) => <i key={b} style={{ flex: book.distribution[b], background: BAND_COLOR[b] }} data-tip={`${t(`health.band.${b}`)} · ${book.distribution[b]}`} />)}
            </div>
            <div className="legend">{(['strong', 'steady', 'watch', 'at_risk'] as const).map((b) => <span key={b}><i style={{ background: BAND_COLOR[b] }} />{t(`health.band.${b}`)} <b className="num">{book.distribution[b]}</b></span>)}</div>
            <div className="tiny muted">{t('health.book_hint', { count: book.clients })}</div>
          </> : <Empty icon="insights">{t('health.book_empty')}</Empty>}
        </section>
      </div>

      <div className="deck-grid">
        <div className="stack" style={{ gap: 18 }}>
          {hasPeople && stars.length > 0 && (
            <section className="card" aria-labelledby="const-title">
              <div className="card-head"><div><h2 id="const-title">{t('constellation.title')}</h2><div className="small muted">{t('constellation.subtitle')}</div></div><span className="tiny muted">{t('constellation.hint')}</span></div>
              <div className="constellation-wrap"><Constellation stars={stars} onOpen={openPerson} youLabel={t('constellation.you')} height={340} /></div>
              <div className="legend const-legend">
                <span><i style={{ background: TIER_VAR.now, borderRadius: '50%' }} />{t('constellation.legend_now')} · {stars.filter((s) => s.tier === 'now').length}</span>
                <span><i style={{ background: TIER_VAR.soon, borderRadius: '50%' }} />{t('constellation.legend_soon')} · {stars.filter((s) => s.tier === 'soon').length}</span>
                <span><i style={{ background: TIER_VAR.calm, borderRadius: '50%' }} />{t('constellation.legend_calm')} · {stars.filter((s) => s.tier === 'calm').length}</span>
                <span className="muted">{t('constellation.size')}</span>
              </div>
            </section>
          )}

          <section className="card" aria-labelledby="att-title">
            <div className="card-head">
              <div><h2 id="att-title">{t('today.attention_title')}</h2><div className="small muted">{t('today.attention_hint')}</div></div>
              <span className="chip gold">{grouped.length}</span>
            </div>
            {grouped.length ? (
              <div className="attention-list">
                {visible.map((items) => <AttentionCard key={items[0]!.id} items={items} />)}
                {!showAll && grouped.length > 6 && <button className="btn btn-ghost" onClick={() => setShowAll(true)}>{t('today.show_more', { count: grouped.length - 6 })}</button>}
              </div>
            ) : <Empty icon="spark">{t('today.attention_empty')}</Empty>}
          </section>
          <NextActionsCard limit={5} />
          <LifeRadarCard />
        </div>

        <div className="stack" style={{ gap: 18, alignContent: 'start' }}>
          <section className="card">
            <div className="card-head"><h3>{t('today.agenda_title')}</h3><button className="btn btn-sm btn-ghost" onClick={() => navigate('/agenda')}>{t('common.view_all')}</button></div>
            {todayTasks.length ? <DayTimeline tasks={todayTasks} clock={clock} /> : <Empty icon="agenda">{t('today.agenda_empty')}</Empty>}
            {overdue.length > 0 && <div style={{ padding: '0 20px 14px' }}><button className="chip crit" onClick={() => navigate('/agenda')}>{t('today.overdue_title')} · {overdue.length}</button></div>}
          </section>

          <section className="card">
            <div className="card-head"><h3>{t('today.cases_title')}</h3><button className="btn btn-sm btn-ghost" onClick={() => navigate('/cases')}>{t('common.view_all')}</button></div>
            {cases.length ? <div style={{ padding: '0 10px 10px' }}>{cases.slice(0, 5).map((c) => {
              const p = view.get('persons', c.personId);
              const idx = stageIndex(c.stage);
              return (
                <div key={c.id} className="case-mini" onClick={() => navigate(`/cases/${c.id}`)} role="link" tabIndex={0}>
                  <div className="row spread"><span className="row" style={{ gap: 8, minWidth: 0 }}><Avatar name={p?.displayName ?? ''} size="xs" /><b className="person-name one-line">{p?.displayName}</b></span><span className={`chip ${isStalled(c, today) ? 'high' : 'teal'}`}>{i18n.enumLabel('caseStage', c.stage)} · {t('case.age', { days: caseAgeDays(c, today) })}</span></div>
                  <div className="small muted">{[c.product, c.insurer].filter(Boolean).join(' · ')}</div>
                  <div className="journey">{CASE_MAIN_STAGES.map((s, i) => <i key={s} className={i < idx ? 'on' : i === idx ? 'now' : ''} />)}</div>
                </div>
              );
            })}</div> : <Empty icon="cases">{t('today.cases_empty')}</Empty>}
          </section>

          <section className="card" data-testid="now-decisions">
            <div className="card-head"><h3>{t('today.waiting_title')}</h3><button className="btn btn-sm btn-ghost" onClick={() => navigate('/decisions')}>{t('common.view_all')}</button></div>
            {decisions.length || waiting.length ? <div className="agenda-list">
              {decisions.slice(0, 4).map((d) => (
                <div key={`${d.kind}${d.id}`} className="agenda-item" style={{ gridTemplateColumns: 'auto minmax(0,1fr) auto' }} onClick={() => navigate(d.kind === 'comparison' ? `/comparator/${d.id}` : d.kind === 'quotation' ? '/quotes' : '/pipeline')} role="button" tabIndex={0}>
                  <span className="kind-icon"><Icon name={d.kind === 'comparison' ? 'compare' : d.kind === 'quotation' ? 'document' : 'flame'} /></span>
                  <div style={{ minWidth: 0 }}><div className="t one-line">{d.title}</div><div className="s one-line">{[view.get('persons', d.personId ?? '')?.displayName, t('decisions.waiting_days', { days: d.days })].filter(Boolean).join(' · ')}</div></div>
                  {d.deadline ? <span className="chip high">{i18n.date(d.deadline, 'dayMonth')}</span> : <span />}
                </div>
              ))}
              {waiting.slice(0, 3).map((w) => (
                <div key={w.id} className="agenda-item" style={{ gridTemplateColumns: 'auto minmax(0,1fr) auto' }} onClick={() => openers(rt).complete(w)} role="button" tabIndex={0}>
                  <span className="kind-icon"><Icon name="followup" /></span>
                  <div style={{ minWidth: 0 }}><div className="t one-line">{w.title}</div><div className="s one-line">{t('today.waiting_client', { name: view.get('persons', w.personId ?? '')?.displayName ?? '' })}</div></div>
                  <span className="tiny muted">{w.dueDate ? i18n.date(w.dueDate, 'dayMonth') : ''}</span>
                </div>
              ))}
            </div> : <Empty icon="check">{t('decisions.none')}</Empty>}
          </section>

          <section className="card">
            <div className="card-head"><h3>{t('today.moments_title')}</h3></div>
            {moments.length ? <div className="agenda-list">{moments.slice(0, 6).map((m) => {
              const p = m.personId ? view.get('persons', m.personId) : undefined;
              return (
                <div key={m.id} className="agenda-item" style={{ gridTemplateColumns: 'auto minmax(0,1fr) auto' }} onClick={() => performAction(rt, m)} role="button" tabIndex={0}>
                  <Avatar name={p?.displayName ?? ''} size="sm" />
                  <div style={{ minWidth: 0 }}><div className="t person-name one-line">{p?.displayName}</div><div className="s">{reasonText(i18n, m)}</div></div>
                  <Icon name="cake" size={16} />
                </div>
              );
            })}</div> : <Empty icon="cake">{t('today.moments_empty')}</Empty>}
          </section>
        </div>
      </div>
    </div>
  );
}

/** A briefing sentence with people rendered as live chips (click → Relationship Focus). */
function renderLine(rt: Runtime, l: BriefingLine): ReactNode {
  const { i18n } = rt;
  const params: Record<string, string | number> = {};
  const MARK = '\u0001';
  for (const [k, v] of Object.entries(l.params)) {
    if (l.refs[k]) params[k] = `${MARK}${k}${MARK}`;
    else if (k === 'time' && typeof v === 'string' && /^\d{2}:\d{2}$/.test(v)) params[k] = i18n.time(v);
    else params[k] = typeof v === 'string' ? i18n.maybe(v) : v;
  }
  const text = i18n.t(l.key, params);
  return text.split(MARK).map((part, i) => {
    if (i % 2 === 0) return <Fragment key={i}>{part}</Fragment>;
    const id = l.refs[part];
    const name = String(l.params[part] ?? '');
    return id ? <button key={i} className="name-chip" onClick={() => navigate(personPath(id))} data-testid="briefing-name"><Avatar name={name} size="xs" />{name}</button> : <Fragment key={i}>{name}</Fragment>;
  });
}

function buildStars(view: Runtime['view'], i18n: Runtime['i18n'], attention: AttentionItem[], today: string): Star[] {
  const lead = new Map<string, AttentionItem>();
  for (const a of attention) if (a.personId && (!lead.get(a.personId) || lead.get(a.personId)!.score < a.score)) lead.set(a.personId, a);
  const rows = view.rows('persons').filter((p) => !p.archivedAt && p.stage !== 'dependent' && (p.stage === 'client' || lead.has(p.id)));
  const all = rows.map((p) => {
    const a = lead.get(p.id);
    const value = view.where('policies', 'personId', p.id).filter(isActivePolicy).reduce((s, x) => s + annualNow(view, x, today), 0);
    const tier: Star['tier'] = !a ? 'calm' : a.severity === 'critical' || a.severity === 'high' ? 'now' : a.severity === 'medium' ? 'soon' : 'calm';
    return { id: p.id, name: p.displayName, tier, value, reason: a ? reasonText(i18n, a) : undefined, sub: value ? `${i18n.money(value)} · ${i18n.enumLabel('stage', p.stage)}` : i18n.enumLabel('stage', p.stage) };
  });
  // keep the map readable on large books: everyone who needs you + the most valuable calm relationships
  const urgent = all.filter((s) => s.tier !== 'calm').sort((a, b) => b.value - a.value).slice(0, 90);
  const calm = all.filter((s) => s.tier === 'calm').sort((a, b) => b.value - a.value).slice(0, Math.max(0, 150 - urgent.length));
  return [...urgent, ...calm];
}

function DayTimeline({ tasks, clock }: { tasks: Task[]; clock: string }) {
  const rt = useRuntime();
  const firstAfter = tasks.findIndex((x) => (x.dueTime ?? '00:00') > clock);
  const nowAt = firstAfter === -1 ? tasks.length : firstAfter;
  return (
    <div className="daytl">
      <ol>
        {tasks.map((task, i) => (
          <Fragment key={task.id}>
            {i === nowAt && <NowLine clock={clock} />}
            <TimelineRow task={task} past={(task.dueTime ?? '00:00') < clock} />
          </Fragment>
        ))}
        {nowAt === tasks.length && <NowLine clock={clock} />}
      </ol>
      <span className="sr-only">{rt.i18n.time(clock)}</span>
    </div>
  );
}
function NowLine({ clock }: { clock: string }) {
  const rt = useRuntime();
  return <li className="nowline" aria-hidden="true" style={{ cursor: 'default' }}><time>{rt.i18n.time(clock)}</time><span className="node" /><hr /></li>;
}
function TimelineRow({ task, past }: { task: Task; past: boolean }) {
  const rt = useRuntime();
  const p = task.personId ? rt.view.get('persons', task.personId) : undefined;
  const pr = task.prospectId ? rt.view.get('prospects', task.prospectId) : undefined;
  const done = task.status !== 'open';
  return (
    <li className={`${task.kind}${done ? ' done' : ''}${past ? ' past' : ''}`} data-testid="agenda-item" onClick={() => (done ? p && navigate(personPath(p.id)) : openers(rt).complete(task))} role="button" tabIndex={0}>
      <time>{task.dueTime ? rt.i18n.time(task.dueTime) : '—'}</time>
      <span className="node" />
      <div style={{ minWidth: 0 }}>
        <div className="t person-name one-line">{rt.i18n.maybe(task.title)}</div>
        <div className="s person-name one-line">{[rt.i18n.enumLabel('taskKind', task.kind), p?.displayName ?? (pr ? rt.t('task.prospect', { name: pr.name }) : ''), task.location].filter(Boolean).join(' · ')}</div>
      </div>
      <span className="kind-icon"><Icon name={done ? 'check' : KIND_ICON[task.kind] ?? 'task'} /></span>
    </li>
  );
}

export function AgendaRow({ task }: { task: Task }) {
  const rt = useRuntime();
  const p = task.personId ? rt.view.get('persons', task.personId) : undefined;
  const pr = task.prospectId ? rt.view.get('prospects', task.prospectId) : undefined;
  const done = task.status !== 'open';
  return (
    <div className={`agenda-item${done ? ' done' : ''}`} data-testid="agenda-item" onClick={() => (done ? p && navigate(personPath(p.id)) : openers(rt).complete(task))} role="button" tabIndex={0}>
      <time>{task.dueTime ? rt.i18n.time(task.dueTime) : '—'}</time>
      <div style={{ minWidth: 0 }}>
        <div className="t person-name one-line">{rt.i18n.maybe(task.title)}</div>
        <div className="s person-name one-line">{[rt.i18n.enumLabel('taskKind', task.kind), p?.displayName ?? (pr ? rt.t('task.prospect', { name: pr.name }) : ''), task.location].filter(Boolean).join(' · ')}</div>
      </div>
      {!done && <VideoButton task={task} small />}
      <span className="kind-icon"><Icon name={done ? 'check' : KIND_ICON[task.kind] ?? 'task'} /></span>
    </div>
  );
}
